import React, { useEffect, useState } from "react";
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, orderBy, serverTimestamp,
    where
} from "firebase/firestore";
import { db } from "../../firebase";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, TablePagination, InputAdornment,
    Grid
} from "@mui/material";
import { Plus, Search, Edit3, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";

function Customerstatus() {

    const { value } = useParams();


    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loanPlans, setLoanPlans] = useState([]);

    const [formData, setFormData] = useState({
        id: null,
        loanId: "",
        category: "",
        amount: "",
        tenure: "",
        interest: "",

        customerName: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        aadharNo: "",
        panNo: "",

        status: "Active"
    });

    // 🔹 Fetch Data
    const fetchData = async () => {
        const q = query(collection(db, "customer_loans"), where('status', '==', value), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setData(arr);
    };

    const fetchLoanPlans = async () => {
        const snap = await getDocs(collection(db, "loans"));
        const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setLoanPlans(arr);
    };

    useEffect(() => {
        if (value) {
            fetchData();
        }
    }, [value]); // ✅ important

    // 🔹 EMI Calculation
    const calculateEMI = (P, rate, N) => {
        const R = rate / 12 / 100;
        const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
        return Math.round(emi || 0);
    };

    const showError = (msg) => {
        setOpen(false); // close dialog first

        setTimeout(() => {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonColor: "#d33",
            });
        }, 200);
    };

    const handleSubmit = async () => {
        // 🔴 VALIDATION
        if (!formData.customerName?.trim()) {
            return showError("Customer Name required");
        }

        if (!formData.mobile || formData.mobile.length !== 10) {
            return showError("Valid 10 digit mobile required");
        }

        if (!formData.loanId) {
            return showError("Select Loan Plan");
        }

        if (!formData.amount) {
            return showError("Amount required");
        }

        if (!formData.aadharNo || formData.aadharNo.length !== 12) {
            return showError("Valid 12 digit Aadhar required");
        }

        if (!formData.panNo) {
            return showError("PAN number required");
        }

        // Mobile only numbers
        if (!/^[0-9]{10}$/.test(formData.mobile)) {
            return showError("Enter valid 10 digit mobile");
        }

        // PAN format
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNo)) {
            return showError("Invalid PAN format (ABCDE1234F)");
        }

        try {
            const payload = {
                loanId: formData.loanId,
                category: formData.category,
                amount: Number(formData.amount),
                tenure: Number(formData.tenure),
                interest: Number(formData.interest),

                customerName: formData.customerName,
                mobile: formData.mobile,
                email: formData.email,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                aadharNo: formData.aadharNo,
                panNo: formData.panNo,

                status: formData.status,
                updatedAt: serverTimestamp()
            };

            if (formData.id) {
                await updateDoc(doc(db, "customer_loans", formData.id), payload);
            } else {
                await addDoc(collection(db, "customer_loans"), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
            }

            setOpen(false);
            resetForm();
            fetchData();

            Swal.fire("Success", "Saved successfully", "success");

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            customerName: "",
            mobile: "",
            loanAmount: "",
            interest: "",
            tenure: "",
            emiAmount: "",
            startDate: "",
            nextDueDate: "",
            paidAmount: 0,
            remainingAmount: 0,
            status: "Active"
        });
    };

    // 🔹 Delete
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete?",
            showCancelButton: true,
            didOpen: () => {
                const swalContainer = document.querySelector('.swal2-container');
                if (swalContainer) {
                    swalContainer.style.zIndex = 2000;
                }
            }
        });

        if (confirm.isConfirmed) {
            await deleteDoc(doc(db, "customer_loans", id));
            fetchData();
        }
    };

    // 🔹 Search
    const filtered = data.filter(d =>
        d.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        d.mobile?.includes(search)
    );

    useEffect(() => {
        setPage(0);
    }, [search]);

    return (
        <Box p={1}>

            {/* HEADER */}
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h5">{value} Loan Customers</Typography>
                {/* <Button variant="contained" startIcon={<Plus />} onClick={() => setOpen(true)}>
                    Add Loan
                </Button> */}
            </Box>

            {/* SEARCH */}
            <TextField
                fullWidth
                size="small"
                placeholder="Search name / mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search size={16} />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />

            {/* TABLE */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Interest</TableCell>
                                <TableCell>Tenure</TableCell>
                                <TableCell>City</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filtered
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, i) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>

                                        <TableCell>{row.customerName}</TableCell>
                                        <TableCell>{row.mobile}</TableCell>
                                        <TableCell>{row.category}</TableCell>

                                        <TableCell>₹{row.amount}</TableCell>
                                        <TableCell>{row.interest}%</TableCell>
                                        <TableCell>{row.tenure} M</TableCell>

                                        <TableCell>{row.city}</TableCell>

                                        <TableCell>
                                            <span style={{
                                                color:
                                                    row.status === "Active" ? "green" :
                                                        row.status === "Closed" ? "blue" :
                                                            "red",
                                                fontWeight: "600"
                                            }}>
                                                {row.status}
                                            </span>
                                        </TableCell>

                                        <TableCell>
                                            {row.createdAt?.seconds
                                                ? new Date(row.createdAt.seconds * 1000).toLocaleDateString()
                                                : "-"}
                                        </TableCell>

                                        <TableCell>
                                            <IconButton onClick={() => {
                                                setFormData({
                                                    ...row,
                                                    loanId: row.loanId || ""
                                                });
                                                setOpen(true);
                                            }}>
                                                <Edit3 size={16} />
                                            </IconButton>

                                            <IconButton onClick={() => handleDelete(row.id)}>
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    count={filtered.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* MODAL */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{formData.id ? "Edit Loan" : "Add Loan"}</DialogTitle>

                <DialogContent>
                    <Grid className="row" spacing={2} mt={1}>

                        {/* Customer Info */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Customer Name" size="small" fullWidth
                                value={formData.customerName}
                                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Mobile" size="small" fullWidth
                                value={formData.mobile}
                                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Email" size="small" fullWidth
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="PAN No" size="small" fullWidth
                                value={formData.panNo}
                                onChange={(e) => setFormData({ ...formData, panNo: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Aadhar No" size="small" fullWidth
                                value={formData.aadharNo}
                                onChange={(e) => setFormData({ ...formData, aadharNo: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField label="Address" size="small" fullWidth
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="City" size="small" fullWidth
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="State" size="small" fullWidth
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="Pincode" size="small" fullWidth
                                value={formData.pincode}
                                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                            />
                        </Grid>

                        {/* Loan Plan */}
                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField
                                select
                                size="small"
                                label="Select Loan Plan"
                                fullWidth
                                value={formData.loanId}
                                onChange={(e) => {
                                    const plan = loanPlans.find(p => p.id === e.target.value);
                                    if (plan) {
                                        setFormData({
                                            ...formData,
                                            loanId: plan.id,
                                            category: plan.category,
                                            amount: plan.amount,
                                            tenure: plan.tenure,
                                            interest: plan.interest
                                        });
                                    }
                                }}
                                SelectProps={{ native: true }}
                            >
                                <option value="">Select Loan</option>
                                {loanPlans.map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.category} - ₹{plan.amount}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Loan Fields */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="Amount" size="small" fullWidth value={formData.amount} />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="Interest" size="small" fullWidth value={formData.interest} />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="Tenure" size="small" fullWidth value={formData.tenure} />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField
                                select
                                label="Status"
                                size="small"
                                fullWidth
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="Active">Active</option>
                                <option value="Pending">Pending</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Closed">Closed</option>
                                <option value="Default">Default</option>

                            </TextField>
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Customerstatus;